import React, { useEffect, useMemo, useState } from "react";
import { Wrench, Info, GitCompare, X, Lock } from "lucide-react";
import { LiveChip } from "./views/_shared";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

// Highway ranges run ~90 % of the EPA blended figure at ~65 mph in mild
// weather — a widely-cited approximation across the EV community and
// consistent with Tesla owners' real-world highway data. We only use
// this factor when we have to derive highway range from the EPA blended
// baseline; the primary path uses the vehicle's live rated range so the
// user sees numbers grounded in their actual pack, not a spec sheet.
const HIGHWAY_FACTOR = 0.9;

// Aero drag scales with velocity² — the coefficient of drag is fixed,
// but the *power* required to overcome drag scales with v². Mod deltas
// are calibrated at the reference speed (65 mph) and re-scaled below.
// Highway-factor also drops with speed because rolling resistance is
// a smaller share of losses at higher speeds. Values below map speed
// to (drag_scale, highway_factor) — drag_scale is (v/65)².
const SPEED_BANDS = [
  { mph: 65, dragScale: 1.00, highwayFactor: 0.90 },
  { mph: 75, dragScale: 1.33, highwayFactor: 0.78 },
  { mph: 80, dragScale: 1.51, highwayFactor: 0.72 },
];

const DEFAULT_SPEED = 65;

/**
 * ModificationsCard — aero-mod range-impact simulator.
 *
 * Users toggle common aftermarket body mods (splitter, skirts, spoiler,
 * wheel covers, wide-body) and see the projected % impact on highway
 * range, plus an "adjusted range" figure at 100 % state-of-charge.
 *
 * Delta values are drawn from published aero coefficients (Munro & Assoc.
 * teardown data, Tesla Model 3 Aero disc wind-tunnel numbers, and general
 * SAE studies for aftermarket body kits). They're highway-biased; city
 * range is largely unaffected by aero because average speeds sit below
 * the drag-dominant regime.
 */
const MODS = [
  {
    id: "front_splitter",
    label: "Front splitter",
    desc: "Reduces front-end lift and underbody turbulence.",
    // Small aero win at speed — most kits recoup ~1% highway range.
    highway_delta_pct: 1.0,
    accent: "#39FF14",
  },
  {
    id: "side_skirts",
    label: "Side skirts",
    desc: "Channels air along the underbody, cutting side turbulence.",
    highway_delta_pct: 0.7,
    accent: "#39FF14",
  },
  {
    id: "rear_spoiler",
    label: "Rear spoiler",
    desc: "Aggressive aftermarket spoilers add downforce — and drag.",
    // Add-on aggressive spoilers cost ~1.5% highway range vs stock.
    highway_delta_pct: -1.5,
    accent: "#FF6B57",
  },
  {
    id: "wheel_covers",
    label: "Aero wheel covers",
    desc: "Disc-style wheel covers cut wheel-well turbulence dramatically.",
    // Model 3 Aero disc covers: EPA showed ~+3.5% highway range.
    highway_delta_pct: 3.5,
    accent: "#39FF14",
  },
  {
    id: "wide_body_kit",
    label: "Wide body kit",
    desc: "Bigger frontal area = bigger drag penalty at freeway speed.",
    highway_delta_pct: -6.0,
    accent: "#FF6B57",
  },
  // Custom rims & tires — one size at a time (mutually exclusive group).
  // Deltas reflect non-aero aftermarket wheels + wider tires vs the
  // factory aero setup: rolling resistance + wheel-well turbulence.
  // Consistent with Tesla's own EPA splits between wheel configs.
  {
    id: "rims_19",
    label: "19\" custom rims & tires",
    desc: "Closest to stock — mild turbulence penalty without aero covers.",
    highway_delta_pct: -2.0,
    accent: "#FF6B57",
    group: "wheels",
  },
  {
    id: "rims_20",
    label: "20\" custom rims & tires",
    desc: "Wider contact patch adds rolling resistance at speed.",
    highway_delta_pct: -3.5,
    accent: "#FF6B57",
    group: "wheels",
  },
  {
    id: "rims_21",
    label: "21\" custom rims & tires",
    desc: "Sportier sidewall and open spokes cost real highway miles.",
    highway_delta_pct: -5.0,
    accent: "#FF6B57",
    group: "wheels",
  },
  {
    id: "rims_22",
    label: "22\" custom rims & tires",
    desc: "Maximum look, maximum drag — the biggest wheel penalty.",
    highway_delta_pct: -7.0,
    accent: "#FF6B57",
    group: "wheels",
  },
];

const storageKey = (vin) => `eviq.mods.${vin || "anon"}`;

// Preset configurations for the Compare view. Each preset lists the
// mod IDs it enables so we can compute an adjusted range side-by-side
// against the current selection.
const PRESETS = [
  { id: "stock",       label: "Stock",              mods: [] },
  { id: "eco_aero",    label: "Eco Aero",           mods: ["front_splitter", "wheel_covers"] },
  { id: "street",      label: "Street Aero",        mods: ["front_splitter", "side_skirts", "wheel_covers"] },
  { id: "show_car",    label: "Show / Wide Body",   mods: ["front_splitter", "side_skirts", "rear_spoiler", "wide_body_kit"] },
];

export const ModificationsCard = ({ vehicle }) => {
  const vin = vehicle?.vin || vehicle?.id || null;
  const isLive = !!vehicle?.live;

  // Baseline: for a Tesla-authenticated user we use the live rated
  // range at 100 % SoC (accounts for this specific pack's degradation),
  // scaled by the highway efficiency factor. That way an owner with a
  // 92 % SoH Model 3 sees mod projections against ~290 mi highway, not
  // a pristine 353 mi EPA number that ignores their pack's history.
  const liveRangeAt100 = vehicle?.liveRangeAt100 || null;
  const epaMi = vehicle?.epaRange || null;
  const baselineMi = liveRangeAt100
    ? Math.round(liveRangeAt100 * HIGHWAY_FACTOR)
    : epaMi
    ? Math.round(epaMi * HIGHWAY_FACTOR)
    : 0;
  // Label the source so the user sees clearly whose numbers we're using.
  const baselineSource = liveRangeAt100
    ? "live"
    : epaMi
    ? "epa"
    : "none";
  const vehicleLabel = isLive && vehicle
    ? [vehicle.year, vehicle.model, vehicle.trim].filter(Boolean).join(" ")
    : null;

  // Load previously-selected mods from localStorage so the estimate
  // persists across app opens (same vehicle → same mods). Server-side
  // storage kicks in below and reconciles once fetched.
  const [selected, setSelected] = useState(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = localStorage.getItem(storageKey(vin));
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });

  // Track whether we've done the initial server fetch — before that,
  // we intentionally suppress the debounced save so we don't clobber a
  // freshly-loaded server value with the previous localStorage cache.
  const [serverHydrated, setServerHydrated] = useState(false);

  // 1. On mount (or when vin changes) fetch server-side saved mods.
  //    If the server has any, they win over localStorage cache.
  useEffect(() => {
    if (!vin || !API_BASE) {
      setServerHydrated(true);
      return;
    }
    let cancelled = false;
    fetch(`${API_BASE}/api/mods/${encodeURIComponent(vin)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const serverMods = data.mods || {};
        // Only override local state if the server has any saved keys.
        if (Object.keys(serverMods).length > 0) {
          setSelected(serverMods);
        }
      })
      .catch(() => { /* offline — keep local cache */ })
      .finally(() => {
        if (!cancelled) setServerHydrated(true);
      });
    return () => { cancelled = true; };
  }, [vin]);

  // 2. Persist locally always (offline cache), and once we've hydrated
  //    from the server, also POST back to the server on each change
  //    (debounced) so it follows the account across devices.
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      localStorage.setItem(storageKey(vin), JSON.stringify(selected));
    } catch {
      /* localStorage full or blocked — silently ignore */
    }
    if (!serverHydrated || !vin || !API_BASE) return;
    const t = setTimeout(() => {
      fetch(`${API_BASE}/api/mods/${encodeURIComponent(vin)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mods: selected }),
      }).catch(() => { /* offline — the localStorage cache is enough */ });
    }, 600);
    return () => clearTimeout(t);
  }, [selected, vin, serverHydrated]);

  const { totalDeltaPct, adjustedMi, contributingMods } = useMemo(() => {
    const active = MODS.filter((m) => selected[m.id]);
    const totalDeltaPct = active.reduce((sum, m) => sum + m.highway_delta_pct, 0);
    const adjustedMi = baselineMi
      ? Math.round(baselineMi * (1 + totalDeltaPct / 100))
      : 0;
    return { totalDeltaPct, adjustedMi, contributingMods: active };
  }, [selected, baselineMi]);

  const deltaColor =
    totalDeltaPct > 0.05 ? "#39FF14"
      : totalDeltaPct < -0.05 ? "#FF6B57"
      : "#7FD1FF";

  const anyActive = contributingMods.length > 0;

  // Compare-configurations modal state.
  const [compareOpen, setCompareOpen] = useState(false);

  // Build "your current" as an implicit preset so users can see how it
  // stacks against the named presets.
  const currentPreset = useMemo(
    () => ({
      id: "current",
      label: "Your current",
      mods: MODS.filter((m) => selected[m.id]).map((m) => m.id),
    }),
    [selected]
  );

  const evaluatePreset = (modIds) => {
    const active = MODS.filter((m) => modIds.includes(m.id));
    const delta = active.reduce((s, m) => s + m.highway_delta_pct, 0);
    const mi = baselineMi ? Math.round(baselineMi * (1 + delta / 100)) : 0;
    return { delta, mi, active };
  };

  return (
    <div
      className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 relative"
      data-testid="modifications-card"
    >
      {isLive && (
        <div className="absolute top-2 right-2">
          <LiveChip testid="mods-live" />
        </div>
      )}
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center border shrink-0"
          style={{ background: "rgba(0,229,255,0.14)", borderColor: "rgba(0,229,255,0.5)" }}
        >
          <Wrench className="w-4 h-4" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
            Aero modifications
          </div>
          <div className="text-sm text-white font-semibold leading-snug">
            How mods change your highway range
          </div>
          {vehicleLabel && (
            <div
              className="text-[10px] text-zinc-500 font-mono mt-0.5 truncate"
              data-testid="mods-vehicle-label"
            >
              {vehicleLabel}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => setCompareOpen(true)}
          disabled={!baselineMi || !isLive}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-white hover:bg-white/[0.05] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ borderColor: "rgba(0,229,255,0.35)", background: "rgba(0,229,255,0.06)" }}
          data-testid="mods-open-compare"
          aria-label="Compare mod configurations"
        >
          <GitCompare className="w-3 h-3" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
          Compare
        </button>
      </div>

      {/* Tesla-auth gate. Aero mod projections are only meaningful when
          tied to a specific pack — otherwise the numbers would be a
          decorative EPA reference. Force the user to sign in first. */}
      {!isLive && (
        <div
          className="rounded-xl border p-4 flex items-start gap-3 mb-3"
          style={{
            borderColor: "rgba(0,229,255,0.35)",
            background: "rgba(0,229,255,0.06)",
          }}
          data-testid="mods-locked"
        >
          <Lock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#00E5FF" }} strokeWidth={2.4} />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-white font-medium leading-snug">
              Sign in with Tesla to unlock this card
            </div>
            <div className="text-[11px] text-zinc-400 leading-relaxed mt-1">
              We match your car&#39;s exact make, model, and current pack condition
              through the Tesla Fleet API, then project how each mod would change
              <span className="text-white"> your</span> highway range — not a
              factory spec.
            </div>
            <a
              href={`${API_BASE}/api/tesla/authorize?return_to=/app`}
              data-testid="mods-tesla-signin"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full brand-gradient text-black font-semibold px-3.5 py-1.5 text-[11px]"
            >
              Sign in with Tesla
            </a>
          </div>
        </div>
      )}

      {/* Live totals */}
      <div
        className="rounded-xl border p-3 mb-3 flex items-center justify-between gap-3"
        style={{
          borderColor: anyActive ? `${deltaColor}44` : "rgba(255,255,255,0.08)",
          background: anyActive ? `${deltaColor}0d` : "rgba(255,255,255,0.02)",
        }}
        data-testid="mods-total"
      >
        <div className="min-w-0">
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
            Highway range · adjusted{baselineSource === "live" ? " · your car" : baselineSource === "epa" ? " · EPA baseline" : ""}
          </div>
          <div className="font-display text-xl font-semibold text-white leading-tight" data-testid="mods-adjusted-range">
            {baselineMi ? `${adjustedMi} mi` : "—"}
            {baselineMi ? (
              <span className="text-xs text-zinc-500 font-mono ml-1.5">
                from {baselineMi} mi
              </span>
            ) : null}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
            Δ vs stock
          </div>
          <div
            className="font-display text-xl font-semibold leading-tight"
            style={{ color: deltaColor }}
            data-testid="mods-total-delta"
          >
            {totalDeltaPct > 0 ? "+" : ""}
            {totalDeltaPct.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Mod toggles — disabled until Tesla-authenticated */}
      <div className={`space-y-2 ${!isLive ? "opacity-50 pointer-events-none" : ""}`} data-testid="mods-list" aria-disabled={!isLive}>
        {MODS.map((m) => {
          const on = !!selected[m.id];
          const sign = m.highway_delta_pct > 0 ? "+" : "";
          return (
            <button
              key={m.id}
              type="button"
              onClick={() =>
                setSelected((s) => {
                  const next = { ...s, [m.id]: !s[m.id] };
                  // Mutually exclusive groups (e.g. one wheel size at a time)
                  if (m.group && next[m.id]) {
                    for (const other of MODS) {
                      if (other.group === m.group && other.id !== m.id) next[other.id] = false;
                    }
                  }
                  return next;
                })
              }
              className="w-full text-left rounded-xl border p-3 flex items-center gap-3 transition-colors"
              style={{
                borderColor: on ? `${m.accent}55` : "rgba(255,255,255,0.08)",
                background: on ? `${m.accent}12` : "rgba(255,255,255,0.02)",
              }}
              data-testid={`mod-toggle-${m.id}`}
              aria-pressed={on}
            >
              {/* Custom checkbox */}
              <div
                className="w-4 h-4 rounded shrink-0 flex items-center justify-center border"
                style={{
                  borderColor: on ? m.accent : "rgba(255,255,255,0.25)",
                  background: on ? m.accent : "transparent",
                }}
              >
                {on && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M2 5.5L4 7.5L8 3" stroke="#050505" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] text-white font-medium leading-tight">{m.label}</div>
                <div className="text-[11px] text-zinc-500 leading-snug mt-0.5">{m.desc}</div>
              </div>
              <div className="shrink-0 text-right">
                <div
                  className="font-mono text-[12px] font-semibold"
                  style={{ color: m.highway_delta_pct > 0 ? "#39FF14" : "#FF6B57" }}
                >
                  {sign}
                  {m.highway_delta_pct.toFixed(1)}%
                </div>
                <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                  highway
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Footnote */}
      <div className="mt-3 rounded-lg border border-white/5 bg-white/[0.01] px-3 py-2 flex items-start gap-2" data-testid="mods-footnote">
        <Info className="w-3 h-3 shrink-0 mt-0.5" style={{ color: "#7FD1FF" }} strokeWidth={2.2} />
        <div className="text-[10px] text-zinc-500 leading-snug">
          {baselineSource === "live" ? (
            <>
              Baseline is <span className="text-white">your car&#39;s</span> current rated
              range at 100 % SoC (from {vehicle?.brand === "ford" ? "Ford" : vehicle?.brand === "volvo" ? "Volvo" : "Tesla"}), converted to ~65 mph highway.
              Deltas are from published aero data (Munro teardowns, EPA Aero-wheel
              filings, SAE aftermarket studies).
            </>
          ) : (
            <>
              Highway estimate at ~65 mph based on published aero data (Munro
              teardowns, EPA Aero-wheel filings, SAE aftermarket studies).
              City range is largely unaffected — drag dominates only above ~40 mph.
            </>
          )}
        </div>
      </div>

      {compareOpen && (
        <CompareOverlay
          onClose={() => setCompareOpen(false)}
          baselineMi={baselineMi}
          presets={PRESETS}
          currentPreset={currentPreset}
          evaluatePreset={evaluatePreset}
        />
      )}
    </div>
  );
};

// -------------------------------------------------------------------------
// Compare configurations — side-by-side view of preset + current
// configurations so owners can shop mods before they buy.
// -------------------------------------------------------------------------
const CompareOverlay = ({ onClose, baselineMi, presets, currentPreset, evaluatePreset }) => {
  // Include the user's current configuration first, then the named presets.
  const columns = [currentPreset, ...presets];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm px-2 py-4"
      onClick={onClose}
      data-testid="mods-compare-overlay"
    >
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0a0a] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)]"
        onClick={(e) => e.stopPropagation()}
        data-testid="mods-compare-panel"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 bg-[#0a0a0a]">
          <div>
            <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-zinc-500">
              Compare configurations
            </div>
            <h3 className="font-display text-lg text-white font-semibold leading-tight mt-0.5">
              Stock vs. preset aero packages
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 rounded-full p-1.5 hover:bg-white/[0.06] text-zinc-400 hover:text-white"
            aria-label="Close comparison"
            data-testid="mods-compare-close"
          >
            <X className="w-4 h-4" strokeWidth={2.4} />
          </button>
        </div>

        {/* Compare grid */}
        <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="mods-compare-grid">
          {columns.map((col) => {
            const { delta, mi, active } = evaluatePreset(col.mods);
            const isCurrent = col.id === "current";
            const c =
              delta > 0.05 ? "#39FF14"
                : delta < -0.05 ? "#FF6B57"
                : "#7FD1FF";
            return (
              <div
                key={col.id}
                className="rounded-xl border p-3 flex flex-col"
                style={{
                  borderColor: isCurrent ? "rgba(57,255,20,0.45)" : "rgba(255,255,255,0.10)",
                  background: isCurrent ? "rgba(57,255,20,0.06)" : "rgba(255,255,255,0.02)",
                }}
                data-testid={`mods-compare-col-${col.id}`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                    {col.label}
                  </div>
                  {isCurrent && (
                    <div
                      className="font-mono text-[8px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                      style={{ background: "rgba(57,255,20,0.14)", color: "#39FF14" }}
                    >
                      You
                    </div>
                  )}
                </div>
                <div className="font-display text-xl font-semibold text-white leading-none">
                  {baselineMi ? `${mi}` : "—"}
                  <span className="text-[10px] font-mono text-zinc-500 ml-1">mi hwy</span>
                </div>
                <div
                  className="font-mono text-[11px] font-semibold mt-1"
                  style={{ color: c }}
                >
                  {delta > 0 ? "+" : ""}
                  {delta.toFixed(1)}% vs stock
                </div>

                <div className="mt-3 space-y-1 flex-1">
                  {active.length === 0 ? (
                    <div className="text-[10px] text-zinc-500 italic">No mods</div>
                  ) : (
                    active.map((m) => (
                      <div key={m.id} className="text-[10px] text-zinc-300 flex items-baseline gap-1.5">
                        <span
                          className="w-1 h-1 rounded-full shrink-0 mt-1.5"
                          style={{ background: m.accent }}
                        />
                        <span className="flex-1 truncate">{m.label}</span>
                        <span
                          className="font-mono text-[10px] shrink-0"
                          style={{ color: m.highway_delta_pct > 0 ? "#39FF14" : "#FF6B57" }}
                        >
                          {m.highway_delta_pct > 0 ? "+" : ""}
                          {m.highway_delta_pct.toFixed(1)}%
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="px-5 pb-5 pt-2 text-[10px] text-zinc-500 leading-snug" data-testid="mods-compare-footnote">
          Highway range at 100% state-of-charge, ~65 mph. Compare presets side-by-side
          before you buy parts. Numbers use published aero coefficients — real-world
          results vary with speed, tire type, and driving style.
        </div>
      </div>
    </div>
  );
};
